import {
	computed,
	getCurrentScope,
	onScopeDispose,
	ref,
	toValue,
	watch
} from "vue";
import type { ComputedRef, MaybeRefOrGetter, Ref } from "vue";

/** Delay before sliding on after the silent jump from the clone to the start. */
const LOOP_RESTART_MS = 100;
const DEFAULT_PAUSE_REASON = "default";

export interface VerticalSliderOptions {
	interval?: number;
	/**
	 * Rotation runs only while this is true (reduced motion off, a breakpoint
	 * that shows one item at a time). Off returns to the first item.
	 */
	enabled?: MaybeRefOrGetter<boolean>;
}

export interface VerticalSlider {
	currentIndex: Ref<number>;
	transformStyle: ComputedRef<string>;
	shouldTransition: Ref<boolean>;
	/** Client-only: call from `onMounted`. Later calls are no-ops. */
	startAutoScroll: () => void;
	stop: () => void;
	/**
	 * Holds the rotation for a named reason (hover, focus, touch…); it resumes
	 * only once every reason is released. No reason = one shared default.
	 */
	pause: (reason?: string) => void;
	resume: (reason?: string) => void;
	goTo: (index: number) => void;
}

/**
 * Rotates a vertical strip one item per interval. `items` must end with a copy
 * of the first item: the strip slides onto it, then jumps back to the start
 * without a transition, so the loop looks seamless.
 */
export const useVerticalSlider = (
	items: MaybeRefOrGetter<readonly unknown[]>,
	intervalOrOptions: number | VerticalSliderOptions = 3000
): VerticalSlider => {
	const options =
		typeof intervalOrOptions === "number"
			? { interval: intervalOrOptions }
			: intervalOrOptions;
	const interval = options.interval ?? 3000;
	const isEnabled = computed(() => toValue(options.enabled) ?? true);
	const count = computed(() => toValue(items).length);

	const currentIndex = ref(0);
	const pauseReasons = ref<ReadonlySet<string>>(new Set());
	const isPaused = computed(() => pauseReasons.value.size > 0);
	const shouldTransition = ref(true);

	const transformStyle = computed(
		() => `translateY(-${currentIndex.value * 100}%)`
	);

	let isStarted = false;
	let intervalId: ReturnType<typeof setInterval> | undefined;
	let restartId: ReturnType<typeof setTimeout> | undefined;

	const tick = () => {
		if (isPaused.value) return;
		const last = count.value - 1;
		shouldTransition.value = currentIndex.value !== last;

		if (currentIndex.value !== last) {
			currentIndex.value += 1;
			return;
		}
		currentIndex.value = 0;
		restartId = setTimeout(() => {
			shouldTransition.value = true;
			if (!isPaused.value) currentIndex.value += 1;
		}, LOOP_RESTART_MS);
	};

	const stop = () => {
		clearInterval(intervalId);
		clearTimeout(restartId);
		intervalId = undefined;
		restartId = undefined;
		shouldTransition.value = true;
		currentIndex.value = 0;
	};

	const run = () => {
		if (intervalId || count.value <= 1 || !isEnabled.value) return;
		intervalId = setInterval(tick, interval);
	};

	const startAutoScroll = () => {
		isStarted = true;
		run();
	};

	watch(isEnabled, (enabled) => {
		if (!isStarted) return;
		if (enabled) run();
		else stop();
	});

	// New content (a layout re-fetch, another locale) restarts from the top.
	watch(
		() => toValue(items),
		() => {
			stop();
			if (isStarted) run();
		}
	);

	if (getCurrentScope()) onScopeDispose(stop);

	return {
		currentIndex,
		transformStyle,
		shouldTransition,
		startAutoScroll,
		stop,
		pause: (reason = DEFAULT_PAUSE_REASON) => {
			pauseReasons.value = new Set([...pauseReasons.value, reason]);
		},
		resume: (reason = DEFAULT_PAUSE_REASON) => {
			pauseReasons.value = new Set(
				[...pauseReasons.value].filter((held) => held !== reason)
			);
		},
		goTo: (index: number) => {
			currentIndex.value = Math.min(Math.max(index, 0), count.value - 1);
		}
	};
};
